/**
 * Component to render SCA Managed board page
 */
const ChiefFinancialOfficerPage = (): React.ReactElement => {
  return (
    <div
      className="mb-10 font-public-sans"
      data-testid="chief-financial-officer-page-component"
    >
      <p>
        <strong>Objectives of the Award</strong>
      </p>

      <p>It is to recognise CFOs who:</p>

      <ul className="list-outside list-disc pb-6 pl-8">
        <li>
          Play a significant role in supporting, advocating and promoting good
          corporate governance, disclosure and transparency;
        </li>
        <li>
          Are exemplary role models in corporate financial disclosure by going
          beyond the regulatory financial reporting requirements;
        </li>
        <li>Help create long-term sustainable shareholder value;</li>
        <li>
          Help the business navigate change and advise on strategic direction;
          and
        </li>
        <li>Are effective leaders for the finance and accounting function.</li>
      </ul>

      <p>
        <strong>Categories of the Award</strong>
      </p>

      <p>
        The Award will be presented for the following categories of companies:
      </p>

      <ul className="list-outside list-disc pb-6 pl-8">
        <li>
          Listed Companies with market capitalisation of S$1 billion and above;
        </li>
        <li>
          Listed Companies with market capitalisation of S$300 million to less
          than S$1 billion;
        </li>
        <li>
          Listed Companies with market capitalisation of less than S$300
          million.
        </li>
      </ul>

      <p>
        <strong>Selection and Judging Process</strong>
      </p>

      <p>The selection for this Award goes through a three-stage process.</p>

      <p>
        In Stage 1, an initial list of candidates will be identified through the
        following sources:
      </p>

      <ul className="list-outside list-disc pb-6 pl-8">
        <li>Nominations;</li>
        <li>Media reports and industry knowledge;</li>
        <li>
          Rankings of the CFOs&apos; companies in the Singapore Governance and
          Transparency Index;
        </li>
        <li>Performance of the CFOs&apos; companies.</li>
      </ul>

      <p>
        In stage 2, additional research and inputs on shortlisted CFOs are
        conducted through research and discreet inquiries. A final shortlist is
        then drawn up using the criteria (described below) for the judges to
        assess in stage 3.
      </p>

      <p>
        In stage 3, a panel of judges will use information obtained from earlier
        stages, review and evaluate the final shortlist of candidates. The
        Judging Panel will decide the winners for each category.
      </p>

      <p>
        <strong>Criteria</strong>
      </p>

      <p>
        The corporate context in which the Chief Financial Officer functions
        will be considered in assessing potential nominees, including
        transparency of financial reporting; high standards of corporate
        behaviour, sound accounting practices; commitment to good corporate
        governance consistent with current standards and evolving guidelines;
        providing advice on strategic direction; and being an effective leader
        of the finance and accounting function. In addition, the Best Chief
        Financial Officer nominees will be evaluated on these three areas:
      </p>

      <ul className="list-outside list-disc pb-6 pl-8">
        <li>Personal and Professional Qualities;</li>
        <li>Knowledge and Experience;</li>
        <li>Track Record and Accomplishments.</li>
      </ul>

      <p>
        <strong>Personal and Professional Qualities</strong>
      </p>

      <ul className="list-outside list-disc pb-6 pl-8">
        <li>
          Integrity: The CFO&apos;s integrity must be of the highest standard.
          He/She must exhibit the highest ethical behaviour and inculcate the
          same corporate culture top-down, across all levels within the
          organisation;
        </li>
        <li>
          Transparency: The CFO is someone who champions the need for greater
          accountability and transparency in corporate governance;
        </li>
        <li>
          Soft People Skills: The CFO must have the skills and abilities to
          address and manage the concerns of the various corporate constituents
          – the CEO, directors, unit managers, analysts, bankers, investors and
          the media – in an impartial manner;
        </li>
        <li>
          Confidence: The CFO must have the confidence to provide meaningful
          information about the company&apos;s accounts and finances at board
          meetings and with shareholders, investors and the media;
        </li>
        <li>
          Vision and Leadership: The CFO must possess sound business acumen,
          astute foresight and the ability to lead, inspire and guide his/her
          organisation to greater heights of growth, financial achievement and
          recognition;
        </li>
        <li>
          Innovation: The CFO is characterised as an innovator who has the
          ability to transform and shape the successful financial performance of
          his/her organisation while exercising sound business judgement and
          demonstrating excellence in his/her field.
        </li>
      </ul>

      <p>
        <strong>Knowledge and Experience</strong>
      </p>

      <ul className="list-outside list-disc pb-6 pl-8">
        <li>
          Knowledge Base: The CFO must have a sound understanding of all matters
          relating to the corporate regulatory compliance and reporting
          requirements of the company;
        </li>
        <li>
          Experience: While age is not the sole decisive factor, the CFO should
          ideally have spent at least three years in the finance function and
          three full years in his current company. Preferably the CFO should not
          be a newcomer to the industry in which he/she is currently working in;
        </li>
        <li>
          Professional Training: Possessing the necessary professional
          qualifications in accountancy or finance will be a plus. Accountancy
          and finance designations include the Chartered Accountant (“CA”),
          Certified Public Accountant (“CPA”) and Chartered Financial Analyst
          (“CFA”). Other finance-related tertiary qualifications will also be
          considered;
        </li>
        <li>
          Continuous Learning: The CFO should possess a desire for continuous
          learning and professional development.
        </li>
      </ul>

      <p>
        <strong>Track Record and Accomplishments</strong>
      </p>

      <ul className="list-outside list-disc pb-6 pl-8">
        <li>
          Personal Achievements: The CFO&apos;s track record and accomplishments
          will be a key factor. The judges will consider the corporate deals,
          including M&As, rights issues etc, handled by the CFO during the year
          under review. The degree of difficulty of each corporate deal will
          also be taken into account. The ability of the CFO to create long-term
          shareholder value for the company is an important consideration;
        </li>
        <li>
          Corporate Reporting and Performance: The financial performance of an
          organisation is indicative of success or failure. Full and fair
          disclosure, sound financial management, integrity and a commitment to
          ethical business conduct are key indicators of a good CFO;
        </li>
        <li>
          Social Responsibility: In addition to a commitment to integrity and a
          passion for the future of business, a highly distinguished CFO uses
          his/her influence, skill and creativity to make significant
          contributions to the finance and accounting profession, industry and
          to the community;
        </li>
        <li>
          Global Competitiveness: Companies compete in a global marketplace. A
          successful CFO demonstrates global initiatives and regional drives
          that foster potential new markets abroad, enhance operations, aid in
          the establishment of foreign ventures or the expansion of foreign
          corporate affairs on an international scale. The CFO is a key
          pre-requisite for the success of the company competing in the global
          marketplace.
        </li>
      </ul>

      <div className="mt-6 text-xs">
        <p>
          <strong>For more information</strong>
        </p>

        <ul className="mb-6 list-none">
          <li>Ms Vera Tan</li>
          <li>Email: vera.tan@isca.org.sg</li>
          <li>DID: +65 6597 5672</li>
        </ul>

        <ul className="list-none">
          <li>60 Cecil Street, ISCA House</li>
          <li>Singapore 049709</li>
          <li>Telephone: 65-6597 5525</li>
          <li>Email: soohoon.koh@isca.org.sg</li>
        </ul>
      </div>
    </div>
  );
};

export default ChiefFinancialOfficerPage;
